import { convertCommercialQuantity } from '../../utils/commercialQuantity';
import type { AllocationUnit } from '../../utils/dimensionConversion';
import { normalizeUnitCode } from '../../utils/units';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  rebaseWarehouseUnitPrice,
} from './warehouseQuantityConversion';

export type PZBatchConversionTarget = 'pcs' | 'm2' | 'm3';

export interface PZBatchConvertibleItem {
  quantityOrdered?: number | null;
  unit?: string | null;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  purchaseQuantity?: number | null;
  purchaseUnit?: string | null;
  unitPrice?: number | null;
  totalPrice?: number | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
}

interface PZBatchConversionResult<T extends PZBatchConvertibleItem> {
  items: T[];
  convertedCount: number;
  skippedCount: number;
}

const commercialUnit = (unit: string | null | undefined): AllocationUnit => {
  const normalized = normalizeUnitCode(unit);
  return normalized === 'm2' || normalized === 'm3' ? normalized : 'pcs';
};

/** Change the purchase-price basis for all eligible lines without changing stock. */
export function convertPZItemsToTarget<T extends PZBatchConvertibleItem>(
  items: T[],
  target: PZBatchConversionTarget,
  rebasePrices: boolean,
): PZBatchConversionResult<T> {
  let convertedCount = 0;
  let skippedCount = 0;

  const nextItems = items.map((item) => {
    const physicalQuantity = Number(item.quantityOrdered);
    const physicalUnit = commercialUnit(item.unit);

    if (!Number.isFinite(physicalQuantity) || physicalQuantity <= 0) {
      skippedCount += 1;
      return item;
    }

    let purchaseQuantity: number;
    if (target === physicalUnit) {
      purchaseQuantity = physicalQuantity;
    } else {
      const converted = convertCommercialQuantity(physicalQuantity, physicalUnit, target, item);
      if (
        converted.error
        || converted.value == null
        || converted.value <= 0
        || converted.value > WAREHOUSE_CONVERTED_QUANTITY_MAX
      ) {
        skippedCount += 1;
        return item;
      }
      purchaseQuantity = converted.value;
    }

    const patch = {
      quantity: physicalQuantity,
      unit: physicalUnit,
      pricingQuantity: purchaseQuantity,
      pricingUnit: target,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
    };
    const rebased = rebasePrices
      ? rebaseWarehouseUnitPrice({
          quantity: physicalQuantity,
          unit: physicalUnit,
          pricingQuantity: item.purchaseQuantity,
          pricingUnit: item.purchaseUnit,
          unitPrice: item.unitPrice,
          width: item.width,
          height: item.height,
          length: item.length,
          thickness: item.thickness,
        }, patch)
      : null;

    convertedCount += 1;
    return {
      ...item,
      conversionUnit: target === physicalUnit || target === 'pcs' ? null : target,
      purchaseQuantity,
      purchaseUnit: target,
      ...(rebased ? { unitPrice: rebased.unitPrice, totalPrice: rebased.totalValue } : {}),
    };
  });

  return { items: nextItems, convertedCount, skippedCount };
}
